import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUserEdit, FaCamera, FaKey, FaSave, FaEnvelope, FaShieldAlt, FaMapMarkerAlt, FaUpload, FaLink, FaGraduationCap } from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import '../../SuperAdmin/views/ProfileSection.css'; // Reusing established Super Admin CSS for exact style matching

// Note: Reusing ProfileSection.css to guarantee exact stylistic mirroring as requested.

const TutorProfile = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'Tutor',
        location: '',
        bio: '',
        profileImage: '',
        expertise: '',
        linkedinUrl: '',
        portfolioUrl: ''
    });

    const fileInputRef = React.useRef(null);
    const certInputRef = React.useRef(null);

    const [passwords, setPasswords] = useState({
        current: '',
        newPass: '',
        confirmPass: ''
    });

    // Mock initially for UI development before hooking up to isolated endpoints
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setProfile({
                    name: res.data.name || 'Tutor Name',
                    email: res.data.email || 'tutor@example.com',
                    phone: res.data.phone || '',
                    role: res.data.role || 'Tutor',
                    location: res.data.location || '',
                    bio: res.data.bio || 'Experienced educator with a passion for web technologies.',
                    profileImage: res.data.profileImage || '',
                    expertise: res.data.expertise || '',
                    linkedinUrl: res.data.linkedinUrl || '',
                    portfolioUrl: res.data.portfolioUrl || '',
                    certificationUrl: res.data.certificationUrl || ''
                });
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            } catch (err) {
                console.error("Error fetching profile", err);
                setTimeout(() => setLoading(false), 500);
            }
        };
        fetchProfile();
    }, []);

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleAvatarClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleCertClick = () => {
        if (certInputRef.current) certInputRef.current.click();
    }

    const handleViewCertification = () => {
        if (profile.certificationUrl) {
            window.open(`${API_URL}${profile.certificationUrl}`, '_blank');
        } else {
            showError('No Document', 'You have not uploaded a certification yet.');
        }
    }

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        showLoading('Uploading Image...');
        try {
            const res = await axios.post('/api/users/profile-image', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, profileImage: res.data.profileImage }));
            closeLoading();
            showSuccess('Upload Success', 'Your profile image has been updated.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            console.error('Error uploading image', err);
            closeLoading();
            showError('Upload Failed', 'Could not save profile image.');
        }
    };

    const handleCertUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('certificate', file);

        showLoading('Uploading Certificate securely via SSL...');
        try {
            const res = await axios.post('/api/users/cert-upload', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, certificationUrl: res.data.certificationUrl }));
            closeLoading();
            showSuccess('Upload Success', 'Professional certificate securely uploaded.');
        } catch (err) {
            console.error('Error uploading certificate', err);
            closeLoading();
            showError('Upload Failed', 'There was an error uploading your certificate.');
        }
    }

    const saveProfile = async (e) => {
        e.preventDefault();
        showLoading('Updating your profile...');
        try {
            await axios.put('/api/users/profile', profile, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Profile Updated', 'Your changes have been saved securely.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            console.error(err);
            closeLoading();
            showError('Update Failed', 'There was an error updating your profile details.');
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (passwords.newPass !== passwords.confirmPass) {
            return showError('Validation Error', 'New passwords do not match.');
        }
        showLoading('Updating account security...');
        try {
            await axios.post('/api/auth/change-password', {
                currentPassword: passwords.current,
                newPassword: passwords.newPass
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Security Updated', 'Your password has been changed successfully.');
            setPasswords({ current: '', newPass: '', confirmPass: '' });
        } catch (err) {
            console.error(err);
            closeLoading();
            showError('Security Update Failed', err.response?.data?.message || 'Failed to update password.');
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="profile-section fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Tutor Profile</h1>
                <p className="page-subtitle">Manage your professional identity, public credentials, and secure access.</p>
            </div>

            <div className="profile-grid">
                {/* Left Column: Avatar & Quick Info */}
                <div className="avatar-card text-center">
                    <div className="avatar-wrapper">
                        {profile.profileImage ? (
                            <img src={`${API_URL}${profile.profileImage}`} alt="Profile" className="profile-avatar-large-img" />
                        ) : (
                            <div className="profile-avatar-large">
                                {profile.name ? profile.name.charAt(0).toUpperCase() : 'T'}
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                        />
                        <button className="upload-avatar-btn" title="Upload New Picture" onClick={handleAvatarClick}>
                            <FaCamera />
                        </button>
                    </div>
                    <h2 className="profile-name">{profile.name}</h2>
                    <p className="profile-role">{profile.role}</p>

                    <div className="profile-quick-stats">
                        <div className="stat-row">
                            <FaEnvelope className="stat-icon" />
                            <span>{profile.email}</span>
                        </div>
                        <div className="stat-row">
                            <FaMapMarkerAlt className="stat-icon" />
                            <span>{profile.location || 'Location not set'}</span>
                        </div>
                        <div className="stat-row">
                            <FaShieldAlt className="stat-icon" />
                            <span>Verified Tutor Access</span>
                        </div>
                    </div>
                </div>

                {/* Right Column: General Settings */}
                <div className="form-card general-settings">
                    <h3 className="card-title"><FaUserEdit /> Personal & Professional Info</h3>
                    <form onSubmit={saveProfile} className="profile-form">

                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                type="text" name="name" value={profile.name} onChange={handleProfileChange}
                                className="glass-input" required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email" name="email" value={profile.email}
                                    className="glass-input" readOnly style={{ opacity: 0.7, cursor: 'not-allowed' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="text" name="phone" value={profile.phone} onChange={handleProfileChange}
                                    className="glass-input" placeholder="+91 XXXXX XXXXX"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Expertise / Specialization Tags</label>
                            <input
                                type="text" name="expertise" value={profile.expertise} onChange={handleProfileChange}
                                className="glass-input" placeholder="e.g. React, Node.js, UI/UX"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label><FaLink /> LinkedIn Profile</label>
                                <input
                                    type="url" name="linkedinUrl" value={profile.linkedinUrl} onChange={handleProfileChange}
                                    className="glass-input" placeholder="https://linkedin.com/in/..."
                                />
                            </div>
                            <div className="form-group">
                                <label><FaLink /> Portfolio Website</label>
                                <input
                                    type="url" name="portfolioUrl" value={profile.portfolioUrl} onChange={handleProfileChange}
                                    className="glass-input" placeholder="https://yourportfolio.com"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Professional Bio</label>
                            <textarea
                                name="bio" value={profile.bio} onChange={handleProfileChange} rows="3"
                                className="glass-input resize-none" placeholder="Detail your teaching experience..."
                            ></textarea>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="primary-btn pulse-on-hover">
                                <FaSave style={{ marginRight: '8px' }} /> Save Changes
                            </button>
                        </div>

                    </form>
                </div>

                {/* Bottom Row: Credentials & Security (Side by Side split on large screens) */}
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>

                    <div className="form-card security-card">
                        <h3 className="card-title"><FaGraduationCap /> Professional Credentials</h3>
                        <div className="profile-form">
                            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '16px' }}>
                                Securely upload your teaching certifications, degrees, or licenses. Files are encrypted via SSL.
                            </p>
                            <div className="form-group">
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    style={{ display: 'none' }}
                                    ref={certInputRef}
                                    onChange={handleCertUpload}
                                />
                                <button type="button" className="secondary-btn" onClick={handleCertClick} style={{ width: '100%', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', border: '1px dashed #002C5F', background: 'transparent', color: '#002C5F', borderRadius: '8px', marginBottom: '12px' }}>
                                    <FaUpload /> {profile.certificationUrl ? 'Replace Certification File' : 'Select Certification File'}
                                </button>
                                {profile.certificationUrl && (
                                    <button
                                        type="button"
                                        className="primary-btn sm"
                                        onClick={handleViewCertification}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                    >
                                        <FaGraduationCap style={{ marginRight: '8px' }} /> View Current Certification
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-card security-card">
                        <h3 className="card-title"><FaKey /> Security & Access</h3>
                        <form onSubmit={savePassword} className="profile-form">
                            <div className="form-group">
                                <label>Current Password</label>
                                <input
                                    type="password" name="current" value={passwords.current} onChange={handlePasswordChange}
                                    className="glass-input" required placeholder="Verify current"
                                />
                            </div>
                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password" name="newPass" value={passwords.newPass} onChange={handlePasswordChange}
                                    className="glass-input" required placeholder="Min. 8 chars"
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm New Password</label>
                                <input
                                    type="password" name="confirmPass" value={passwords.confirmPass} onChange={handlePasswordChange}
                                    className="glass-input" required placeholder="Match new"
                                />
                            </div>
                            <div className="form-actions" style={{ marginTop: '16px' }}>
                                <button type="submit" className="primary-btn pulse-on-hover" style={{ width: '100%' }}>
                                    Update Password
                                </button>
                            </div>
                        </form>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default TutorProfile;
